import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Gavel, RotateCcw, Users } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";

function relTime(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Staff-only ban management. Lists every currently banned user with the
 * reason, who banned them and when, plus an Unban action.
 */
export default function AdminUsers() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "owner")) navigate("/");
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/users/banned");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load bans");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "owner") load();
  }, [user, load]);

  const unban = async (b) => {
    if (!confirm(`Unban ${b.name || b.discord_id}?`)) return;
    try {
      await api.post("/admin/users/unban", { user_id: b.user_id });
      toast.success("Unbanned");
      setItems((prev) => prev.filter((x) => x.user_id !== b.user_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unban failed");
    }
  };

  if (loading || !user) return null;
  if (user.role !== "admin" && user.role !== "owner") return null;

  return (
    <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-users-page">
      <SEO title="Banned users" description="Serverlistr banned users." path="/admin/users" noindex />
      <Breadcrumbs items={[{ name: "Staff console", href: "/admin" }, { name: "Banned users" }]} />

      <button
        onClick={() => navigate("/admin")}
        className="mt-2 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back to staff console
      </button>

      <div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-purple-300">
        <Gavel className="w-3.5 h-3.5" /> Staff-only · access control
      </div>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black inline-flex items-center gap-3">
        <Users className="w-8 h-8" /> Banned users
      </h1>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
        Users listed here are blocked from logging in, submitting servers, posting reviews, and filing reports. Their existing content stays visible but is flagged.
      </p>

      {busy && items.length === 0 ? (
        <div className="mt-10 text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="mt-10 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-8 text-center"
          data-testid="bans-empty"
        >
          <div className="font-heading text-lg font-black text-emerald-300">Nobody is banned</div>
          <p className="mt-1 text-sm text-zinc-400">Use the &quot;Ban reporter&quot; button in the Reports queue when you spot abuse.</p>
        </div>
      ) : (
        <ul className="mt-8 space-y-3">
          {items.map((b) => (
            <li
              key={b.user_id}
              data-testid={`ban-${b.user_id}`}
              className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-5 flex items-start gap-4"
            >
              {b.picture ? (
                <img src={b.picture} alt="" className="w-12 h-12 rounded-xl border border-white/10 shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-base font-black">{b.name || "Unknown"}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border bg-red-500/10 border-red-500/40 text-red-300">
                    banned
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">{relTime(b.banned_at)}</span>
                </div>
                <div className="mt-1 text-xs text-zinc-500 truncate">
                  {b.email || "—"} · Discord {b.discord_id || "—"}
                </div>
                <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.02] p-2 text-xs text-zinc-300">
                  <span className="font-semibold text-zinc-400">Reason: </span>{b.reason}
                </div>
                <div className="mt-2 text-[10px] font-mono text-zinc-500">
                  by {b.banned_by || "staff"}
                </div>
              </div>
              <button
                onClick={() => unban(b)}
                data-testid={`unban-${b.user_id}`}
                className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Unban
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
